import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_neon-narrative-hub/artifacts/arog2nr1_grok_video_2026-04-24-15-40-45.mp4";

const Hero = () => {
  const videoRef = useRef(null);
  const [textVisible, setTextVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let textTimer;
    let ctaTimer;
    let firstLoopTriggered = false;

    const scheduleCta = (durationMs) => {
      if (firstLoopTriggered) return;
      firstLoopTriggered = true;
      // CTA appears after the first full video pass (capped 6s min / 9s max)
      const delay = Math.min(Math.max(durationMs, 6000), 9000);
      ctaTimer = setTimeout(() => setCtaVisible(true), delay);
    };

    const onPlaying = () => {
      // Text 0.5s after video starts
      textTimer = setTimeout(() => setTextVisible(true), 500);
    };

    const onLoadedMeta = () => {
      if (v.duration && isFinite(v.duration)) {
        scheduleCta(v.duration * 1000);
      }
    };

    // Hard fallback in case metadata never resolves
    const metaFallback = setTimeout(() => scheduleCta(6000), 2500);

    v.addEventListener("playing", onPlaying, { once: true });
    v.addEventListener("loadedmetadata", onLoadedMeta, { once: true });

    // Autoplay / reveal fallback
    const revealFallback = setTimeout(() => setTextVisible(true), 1200);
    const p = v.play?.();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        setTextVisible(true);
        scheduleCta(6000);
      });
    }

    return () => {
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("loadedmetadata", onLoadedMeta);
      clearTimeout(textTimer);
      clearTimeout(ctaTimer);
      clearTimeout(metaFallback);
      clearTimeout(revealFallback);
    };
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      className="relative w-full h-screen min-h-[100svh] overflow-hidden flex items-center justify-center snap-section"
      data-testid="hero-section"
    >
      {/* Video background */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={VIDEO_SRC}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-video"
      />

      {/* Dark gradient overlay for text readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,5,10,0.55) 0%, rgba(5,5,10,0.35) 40%, rgba(5,5,10,0.75) 100%)",
        }}
      />
      {/* Radial vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 35%, rgba(5,5,10,0.65) 100%)",
        }}
      />

      {/* Subtle HUD scanlines (local to hero, in addition to global) */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
          mixBlendMode: "overlay",
        }}
      />

      {/* Corner HUD micro labels */}
      <div className="absolute top-24 md:top-28 left-5 md:left-10 right-5 md:right-10 flex items-center justify-between z-10 pointer-events-none">
        <span className="micro-label text-white/50" data-testid="hero-hud-left">
          [ CH.01 / 10 ]
        </span>
        <span className="micro-label text-white/50 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
          SIGNAL · LIVE
        </span>
      </div>

      {/* Text content */}
      <div className="relative z-10 text-center px-6 w-full max-w-[1200px] mx-auto">
        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 28, letterSpacing: "0em" }}
          animate={
            textVisible
              ? { opacity: 1, y: 0, letterSpacing: "-0.02em" }
              : { opacity: 0, y: 28, letterSpacing: "0em" }
          }
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black uppercase leading-[0.92] text-white interactive-text interactive-text--white"
          style={{
            fontSize: "clamp(2.6rem, 11vw, 9.5rem)",
            textShadow:
              "0 0 30px rgba(0,240,255,0.28), 0 0 60px rgba(255,0,255,0.14), 0 2px 20px rgba(0,0,0,0.6)",
          }}
          data-testid="hero-title"
        >
          U&apos;TH<span className="text-cyan-400">.</span>TIMES
        </motion.h1>

        {/* Accent bar */}
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          animate={textVisible ? { opacity: 1, width: "140px" } : { opacity: 0, width: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
          className="h-[2px] mx-auto mt-6 md:mt-8"
          style={{
            background:
              "linear-gradient(90deg, transparent, #00F0FF 40%, #FF00FF 80%, transparent)",
            boxShadow: "0 0 12px rgba(0,240,255,0.55)",
          }}
          data-testid="hero-accent-bar"
        />

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 18, letterSpacing: "0.1em" }}
          animate={
            textVisible
              ? { opacity: 1, y: 0, letterSpacing: "0.28em" }
              : { opacity: 0, y: 18, letterSpacing: "0.1em" }
          }
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
          className="mt-6 md:mt-8 font-mono-uth uppercase text-white/80 interactive-text interactive-text--cyan"
          style={{
            fontSize: "clamp(0.7rem, 1.5vw, 1rem)",
            textShadow: "0 0 14px rgba(0,240,255,0.35)",
          }}
          data-testid="hero-tagline"
        >
          India&apos;s Biggest Independent Youth Media Brand
        </motion.p>

        {/* Partner pill CTA — appears after first video loop */}
        <AnimatePresence>
          {ctaVisible && (
            <motion.div
              key="hero-cta"
              initial={{ opacity: 0, y: 14, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 md:mt-12 flex justify-center"
              data-testid="hero-partner-pill-wrap"
            >
              <button
                onClick={scrollToContact}
                className="group relative inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full font-mono-uth uppercase text-[11px] md:text-xs tracking-[0.2em] text-white/90 transition-all duration-300 hover:text-black"
                style={{
                  background: "rgba(10, 12, 20, 0.55)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  border: "1px solid rgba(0,240,255,0.45)",
                  boxShadow:
                    "0 0 24px rgba(0,240,255,0.18), inset 0 0 18px rgba(0,240,255,0.06)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#00F0FF";
                  e.currentTarget.style.boxShadow =
                    "0 0 40px rgba(0,240,255,0.55), 0 0 80px rgba(255,0,255,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(10, 12, 20, 0.55)";
                  e.currentTarget.style.boxShadow =
                    "0 0 24px rgba(0,240,255,0.18), inset 0 0 18px rgba(0,240,255,0.06)";
                }}
                data-testid="hero-partner-pill"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-300" />
                </span>
                Partner With Us
                <ArrowUpRight
                  size={14}
                  strokeWidth={1.8}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={textVisible ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 1, delay: 1.1 }}
        className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 pointer-events-none"
        data-testid="hero-scroll-hint"
      >
        <span className="micro-label text-white/55">SCROLL TO DECODE</span>
        <span className="w-[1px] h-10 bg-gradient-to-b from-cyan-400 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
